import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { marked } from "marked";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Eye, ArrowLeft } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

marked.setOptions({ breaks: true, gfm: true });

const BLANK = {
  title: "",
  slug: "",
  excerpt: "",
  content: "## Start writing…\n\n",
  cover_image_url: "",
  tags: [],
  status: "draft",
  seo_title: "",
  seo_description: "",
};

export default function AdminArticleEdit() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const [form, setForm] = useState(BLANK);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tagsText, setTagsText] = useState("");
  const navigate = useNavigate();
  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    if (isNew) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/articles");
        const found = data.find((a) => a.id === id);
        if (!found) {
          toast.error("Article not found");
          navigate("/admin/articles");
          return;
        }
        setForm(found);
        setTagsText((found.tags || []).join(", "));
      } finally { setLoading(false); }
    })();
  }, [id, isNew, navigate]);

  const save = async (statusOverride) => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Title and content are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: tagsText.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (statusOverride) payload.status = statusOverride;
      if (isNew) {
        const { data } = await api.post("/admin/articles", payload);
        toast.success("Article created");
        navigate(`/admin/articles/${data.id}`);
      } else {
        await api.patch(`/admin/articles/${id}`, payload);
        toast.success("Saved");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="container-app py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-navy" /></div>;

  return (
    <div data-testid="admin-article-edit" className="container-app py-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link to="/admin/articles" className="text-xs section-eyebrow hover:underline inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> All articles</Link>
          <h1 className="font-display text-3xl text-navy mt-2">{isNew ? "New article" : "Edit article"}</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => save("draft")} disabled={saving} data-testid="save-draft" className="btn-outline">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save draft
          </button>
          <button onClick={() => save("published")} disabled={saving} data-testid="publish" className="btn-gold">
            <Save className="w-4 h-4" /> Publish
          </button>
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-4">
          <div>
            <Label className="text-xs">Title</Label>
            <Input value={form.title} onChange={(e) => set("title")(e.target.value)} data-testid="art-title" placeholder="Thesis format for AKTU — 2026 Guide" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Slug (auto-generated from title if blank)</Label>
              <Input value={form.slug || ""} onChange={(e) => set("slug")(e.target.value)} data-testid="art-slug" placeholder="thesis-format-for-aktu" />
            </div>
            <div>
              <Label className="text-xs">Tags (comma separated)</Label>
              <Input value={tagsText} onChange={(e) => setTagsText(e.target.value)} data-testid="art-tags" placeholder="AKTU, Thesis, Formatting" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Excerpt (1–2 lines)</Label>
            <Textarea rows={2} value={form.excerpt || ""} onChange={(e) => set("excerpt")(e.target.value)} data-testid="art-excerpt" />
          </div>
          <div>
            <Label className="text-xs">Cover image URL</Label>
            <Input value={form.cover_image_url || ""} onChange={(e) => set("cover_image_url")(e.target.value)} data-testid="art-cover" placeholder="https://images.pexels.com/…" />
          </div>

          <Tabs defaultValue="write">
            <TabsList>
              <TabsTrigger value="write" data-testid="tab-write">Write (markdown)</TabsTrigger>
              <TabsTrigger value="preview" data-testid="tab-preview"><Eye className="w-3 h-3 mr-1" /> Preview</TabsTrigger>
            </TabsList>
            <TabsContent value="write">
              <Textarea
                rows={22}
                value={form.content}
                onChange={(e) => set("content")(e.target.value)}
                data-testid="art-content"
                className="font-mono text-sm"
              />
              <p className="text-xs text-slate-500 mt-1">
                Supports markdown: {"`# heading`, `## sub`, `**bold**`, `*italic*`, `[link](url)`, `- list`, `> quote`"}, and code blocks.
              </p>
            </TabsContent>
            <TabsContent value="preview">
              <div className="prose-blog border border-slate-200 rounded-sm p-6 bg-white" dangerouslySetInnerHTML={{ __html: marked.parse(form.content || "") }} />
            </TabsContent>
          </Tabs>
        </div>

        <aside className="lg:col-span-4 space-y-4">
          <div className="border border-slate-200 rounded-sm p-4 bg-white">
            <Label className="text-xs">Status</Label>
            <Select value={form.status} onValueChange={set("status")}>
              <SelectTrigger data-testid="art-status" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft (hidden)</SelectItem>
                <SelectItem value="published">Published (public)</SelectItem>
              </SelectContent>
            </Select>
            {form.slug && form.status === "published" && (
              <Link to={`/blog/${form.slug}`} target="_blank" className="mt-3 text-xs text-royal hover:underline block">
                View live → /blog/{form.slug}
              </Link>
            )}
          </div>
          <div className="border border-slate-200 rounded-sm p-4 bg-white">
            <div className="section-eyebrow mb-2">SEO</div>
            <div>
              <Label className="text-xs">SEO title (defaults to title)</Label>
              <Input value={form.seo_title || ""} onChange={(e) => set("seo_title")(e.target.value)} data-testid="art-seo-title" />
            </div>
            <div className="mt-3">
              <Label className="text-xs">Meta description (≤160 chars)</Label>
              <Textarea rows={3} value={form.seo_description || ""} onChange={(e) => set("seo_description")(e.target.value)} data-testid="art-seo-desc" />
              <div className="text-[10px] text-slate-500 mt-1">{(form.seo_description || "").length}/160</div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
